import IconButton from '@mui/material/IconButton';
import { CloseIcon } from '@tyro/icons';
import type { ChangeEvent } from 'react';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';
import { Select, type SelectProps } from '../select';

export type RHFSelectProps<
  TField extends FieldValues,
  TSelectOption extends string | number | object,
> = {
  controlProps: UseControllerProps<TField>;
  canDeleteValue?: boolean;
} & SelectProps<TSelectOption>;

export const RHFSelect = <
  TField extends FieldValues,
  TSelectOption extends string | number | object,
>({
  controlProps,
  canDeleteValue = false,
  ...selectProps
}: RHFSelectProps<TField, TSelectOption>) => {
  const {
    field: { ref, value, onChange, ...restFieldProps },
    fieldState: { error },
  } = useController(controlProps);

  const onRemoveValue = () => {
    const mockEvent = {
      target: {
        value: null,
      },
    } as unknown as ChangeEvent<HTMLInputElement>;
    onChange(mockEvent);
    selectProps.onChange?.(mockEvent);
  };

  const isMultiple =
    selectProps?.SelectProps?.multiple ??
    // @ts-expect-error
    selectProps?.slotProps?.select?.multiple ??
    false;
  const defaultValue = value ?? '';
  const defaultMultipleValue = Array.isArray(value) ? value : [];
  const hasValue = isMultiple
    ? defaultMultipleValue.length > 0
    : Boolean(defaultValue);

  return (
    <Select<TSelectOption>
      {...restFieldProps}
      {...selectProps}
      onChange={(e) => {
        onChange(e);
        selectProps.onChange?.(e);
      }}
      InputProps={{
        ...selectProps.InputProps,
        endAdornment: canDeleteValue && hasValue && (
          <IconButton
            size="small"
            sx={{ mr: 2.5 }}
            type="button"
            onClick={onRemoveValue}
          >
            <CloseIcon />
          </IconButton>
        ),
      }}
      inputRef={ref}
      value={isMultiple ? defaultMultipleValue : defaultValue}
      error={!!error}
      helperText={error?.message}
    />
  );
};
